import { useTheme } from '@flik/shared';
import React from 'react';
import { themeStorage } from '../../../../utils/userStorage';

interface SetThemeContentProps {
  onThemeChanged?: (theme: 'light' | 'dark') => void;
}

const SetThemeContent: React.FC<SetThemeContentProps> = ({
  onThemeChanged,
}) => {
  const { theme, setTheme, provideTailwind } = useTheme();

  const handleThemeChange = async (newTheme: 'light' | 'dark') => {
    setTheme(newTheme);
    // Theme persistence is now handled by the ThemeProvider
    // The themeStorage.set() call is redundant but kept for explicit persistence
    themeStorage.set(newTheme);

    // Call the provided callback if available
    if (onThemeChanged) {
      onThemeChanged(newTheme);
    }
  };

  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    switch (e.key) {
      case 'ArrowUp':
        e.preventDefault();
        e.stopPropagation();
        // Navigate to previous theme option
        setTheme(theme === 'dark' ? 'light' : 'dark');
        break;
      case 'ArrowDown':
        e.preventDefault();
        e.stopPropagation();
        // Navigate to next theme option
        setTheme(theme === 'light' ? 'dark' : 'light');
        break;
      case 'Enter':
      case ' ':
        e.preventDefault();
        e.stopPropagation();
        // Toggle theme
        setTheme(theme === 'light' ? 'dark' : 'light');
        break;
      case 'Escape':
        e.preventDefault();
        e.stopPropagation();
        // Navigate back to search (this will be handled by the parent)
        break;
    }
  };

  return (
    <div className="text-center" onKeyDown={handleKeyDown} tabIndex={0}>
      <div className="space-y-3 mb-6">
        {/* Dark Theme Option */}
        <div
          className={`p-4 rounded-3xl border-2 cursor-pointer transition-colors ${provideTailwind(
            'border-dark-option',
          )} ${provideTailwind('bg-dark-option')}`}
          onClick={() => handleThemeChange('dark')}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div
                className={`w-4 h-4 rounded-full ${provideTailwind(
                  'bg-dark-icon',
                )} ${provideTailwind('border-dark-icon')}`}
              ></div>
              <div className="text-left">
                <div
                  className={`text-sm font-medium ${provideTailwind(
                    'text-dark-option',
                  )}`}
                >
                  Dark mode
                </div>
                <div
                  className={`text-xs ${provideTailwind('text-dark-option')}`}
                >
                  Easy on the eyes
                </div>
              </div>
            </div>
            {theme === 'dark' && (
              <div className={provideTailwind('text-filter-active')}>
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                >
                  <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
                </svg>
              </div>
            )}
          </div>
        </div>

        {/* Light Theme Option */}
        <div
          className={`p-4 rounded-3xl border-2 cursor-pointer transition-colors ${provideTailwind(
            'border-light-option',
          )} ${provideTailwind('bg-light-option')}`}
          onClick={() => handleThemeChange('light')}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div
                className={`w-4 h-4 rounded-full ${provideTailwind(
                  'bg-light-icon',
                )} ${provideTailwind('border-light-icon')}`}
              ></div>
              <div className="text-left">
                <div
                  className={`text-sm font-medium ${provideTailwind(
                    'text-light-option',
                  )}`}
                >
                  Light mode
                </div>
                <div
                  className={`text-xs ${provideTailwind('text-light-option')}`}
                >
                  Bright and clean
                </div>
              </div>
            </div>
            {theme === 'light' && (
              <div className={provideTailwind('text-filter-active')}>
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                >
                  <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
                </svg>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export { SetThemeContent };
